import { useQuery } from "@tanstack/react-query";
import useActiveMerchantId from "src/hooks/use-merchant-id";
import balancesProvider from "src/providers/balance-provider";
import type { ConvertParams } from "src/types";

const requireMerchantId = (merchantId: string | null): string => {
	if (!merchantId) {
		throw new Error("Merchant ID is required");
	}
	return merchantId;
};

export function useExchangeRate(
	ticker: string,
	amount: string,
	enabled: boolean,
) {
	const { merchantId } = useActiveMerchantId();

	const isEnabled =
		enabled &&
		Boolean(merchantId) &&
		Boolean(ticker) &&
		Boolean(amount) &&
		amount !== "0";

	return useQuery({
		queryKey: ["exchange-rate", merchantId ?? "", ticker, amount],
		queryFn: async () => {
			const params = {
				from: ticker,
				amount,
				to: "USD",
			} as ConvertParams;
			const result = await balancesProvider.getCurrencyExchangeRate(
				requireMerchantId(merchantId),
				params,
			);
			return result.convertedAmount;
		},
		enabled: isEnabled,
		staleTime: 30000,
		retry: 1,
		placeholderData: "",
	});
}

export function useAvailableBalance(
	ticker: string,
	amount: string,
	isTest: boolean,
	enabled: boolean,
) {
	const query = useExchangeRate(ticker, amount, enabled);

	if (isTest) {
		return { data: "0", isLoading: false, error: null };
	}

	return query;
}

export function useConvertedAmount(
	ticker: string,
	amount: string,
	isValid: boolean,
) {
	return useExchangeRate(ticker, amount, isValid);
}
